import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Mail, User, Store, Shield } from 'lucide-react';
import { login } from '../../store/slices/authSlice';
import { RootState } from '../../store';
import { authService } from '../../services/authService';
import { useDealerSendOTPMutation, useDealerLoginWithOTPMutation } from '../../store/slices/apiSlice';

import toast from 'react-hot-toast';
import DiasporawiseLogo from '../../assets/DWBRANDblue.png';

// User and Dealer roles
const roles = [
  { label: 'User', value: 'USER', icon: User },
  { label: 'Dealer', value: 'DEALER', icon: Store },
];

const UserLoginForm: React.FC = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, user } = useSelector((state: RootState) => state.auth);

  const [selectedRole, setSelectedRole] = useState('USER');
  const [otpEmail, setOtpEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpStep, setOtpStep] = useState<'email' | 'otp'>('email');
  const [otpLoading, setOtpLoading] = useState(false);
  
  // Dealer-specific API hooks
  const [dealerSendOTP, { isLoading: dealerOtpLoading }] = useDealerSendOTPMutation();
  const [dealerLoginWithOTP, { isLoading: dealerLoginLoading }] = useDealerLoginWithOTPMutation();

  // Check if user is already authenticated and redirect
  useEffect(() => {
    if (token && user) {
      // Redirect based on role
      if (user.role === 'DEALER') {
        navigate('/dealer/dashboard', { replace: true });
      } else if (user.role === 'USER') {
        navigate('/user/profile', { replace: true });
      } else {
        // For admin roles, redirect to home
        navigate('/', { replace: true });
      }
    }
  }, [token, user, navigate]);

  // OTP Login Functions
  const handleOTPEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setOtpLoading(true);
    try {
      let response;
      
      if (selectedRole === 'DEALER') {
        // Use dealer-specific endpoint
        response = await dealerSendOTP({ email: otpEmail }).unwrap();
      } else {
        // Use regular user endpoint
        response = await authService.sendOTP(otpEmail);
      }

      if (response.success && response.status === 200) {
        toast.success(`OTP sent successfully to ${otpEmail}`);
        setOtpStep('otp');
      } else {
        toast.error(response.message || 'Failed to send OTP');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Network error. Please try again.');
      console.error('OTP error:', error);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp) {
      toast.error('Please enter the OTP');
      return;
    }

    if (otp.length !== 4) {
      toast.error('OTP must be 4 digits');
      return;
    }

    setOtpLoading(true);
    try {
      let response: any;
      
      if (selectedRole === 'DEALER') {
        // Use dealer-specific endpoint
        response = await dealerLoginWithOTP({ email: otpEmail, otp }).unwrap();
      } else {
        // Use regular user endpoint
        response = await authService.verifyOTP(otpEmail, otp);
      }

      if (response.success && response.status === 200) {
        // Extract token from response
        let token = '';
        let userName = otpEmail.split('@')[0];
        let userId: string | number | undefined = undefined;

        if (selectedRole === 'DEALER') {
          // Dealer response structure
          token = response.data?.token || '';
          userName = response.data?.dealer?.name || userName;
          userId = response.data?.dealer?.dealer_id || undefined;
        } else {
          // User response structure
          token = response.data || '';
          // Decode JWT token to get user info
          try {
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              userId = payload.userId;
            }
          } catch (decodeError) {
            console.warn('Could not decode token:', decodeError);
          }
        }

        if (!token) {
          toast.error('Login successful but no authentication token received. Please contact support.');
          return;
        }

        dispatch(login({
          user: {
            email: otpEmail,
            role: selectedRole as any,
            name: userName,
            id: userId,
          },
          token: token
        }));

        // Show appropriate message based on verification status
        if (selectedRole === 'DEALER' && response.data?.dealer?.verification_status === 'pending') {
          toast.success('Login successful! Your account is pending verification.');
        } else if (selectedRole === 'DEALER' && response.data?.dealer?.verification_status === 'rejected') {
          toast.success('Login successful! Your account has been rejected. Please update your profile.');
        } else {
          toast.success('Login successful! Welcome!');
        }
        
        // Redirect based on role
        if (selectedRole === 'DEALER') {
          navigate('/dealer/dashboard', { replace: true });
        } else {
          navigate('/user/profile', { replace: true });
        }
      } else {
        toast.error(response.message || "Invalid OTP. Please try again.");
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Invalid OTP. Please try again.');
      console.error('OTP verification error:', error);
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      {/* Main Content */}
      <div className="w-full max-w-md">
        {/* Header Section */}
        <div className="text-center mb-8">
          {/* Back Button */}
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={() => {
                // Check if we can go back in browser history
                if (window.history.length > 1 && document.referrer) {
                  // Try to go back in history
                  navigate(-1);
                } else {
                  // Fallback to home page
                  navigate('/', { replace: true });
                }
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <div className="flex-1"></div>
          </div>

          <Link to="/" className="inline-block mb-4">
            <div className="flex items-center justify-center">
              <img
                src={DiasporawiseLogo}
                alt="Diasporawise Logo"
                className="h-12 w-auto object-contain mr-3 hover:scale-105 transition-transform duration-300"
              />
              <div>
                <h1 className="text-3xl font-bold text-red-600">Diasporawise</h1>
                <p className="text-sm text-gray-600">User & Dealer Portal</p>
              </div>
            </div>
          </Link>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h2>
          <p className="text-gray-600">Sign in to your account</p>
        </div>

          {/* Login Form Card */}
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Role Selection */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Account Type</h3>
            <div className="flex space-x-2">
              {roles?.map((role) => {
                const IconComponent = role.icon;
                return (
                  <button
                    key={role.value}
                    onClick={() => setSelectedRole(role.value)}
                    className={`flex flex-col items-center justify-center flex-1 py-3 px-2 rounded-lg border text-sm font-medium transition-all duration-200 ${selectedRole === role.value
                      ? 'border-gray-400 bg-white text-gray-900'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                  >
                    <IconComponent className="h-5 w-5 mb-1" />
                    <span>{role.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* OTP Login Form */}
          {otpStep === 'email' ? (
            <form onSubmit={handleOTPEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={otpEmail}
                    onChange={(e) => setOtpEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 bg-white"
                    placeholder="user@example.com"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={otpLoading || dealerOtpLoading}
                className="w-full bg-gray-800 text-white py-3 px-4 rounded-lg hover:bg-gray-900 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {(otpLoading || dealerOtpLoading) ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending OTP...</span>
                  </>
                ) : (
                  <>
                    <span>Send OTP</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOTPSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter OTP
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Shield className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 bg-white"
                    placeholder="Enter 4-digit OTP"
                    maxLength={4}
                    pattern="[0-9]{4}"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Check your email ({otpEmail}) for the OTP code
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setOtpStep('email');
                    setOtp('');
                  }}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={otpLoading || dealerLoginLoading}
                  className="flex-1 bg-gray-800 text-white py-3 px-4 rounded-lg hover:bg-gray-900 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {(otpLoading || dealerLoginLoading) ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <span>Verify OTP</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Registration Links */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-center text-gray-600 mb-3">
              Don't have an account?
            </p>
            <div className="flex flex-col gap-2">
              <Link
                to="/dealer/register"
                className="w-full text-center py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium text-sm flex items-center justify-center gap-2"
              >
                <Store className="w-4 h-4" />
                Register as Dealer
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserLoginForm;
